/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { useCallback, useEffect, useState } from 'react';

import type { SupabaseClient } from '@supabase/supabase-js';

import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSupabaseClient } from '@/hooks/useSupabaseClient';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { getErrorMessages } from '@/lib/rateLimitErrorHandler';
import { hasAnyContext } from '@/lib/stellaContextUtils';
import {
  ProductHealthScore,
  StellaRecommendationsCache,
  StellaRecommendationsData,
} from '@/types/stellaRecommendations';

const DEFAULT_HEALTH_SCORE: ProductHealthScore = {
  score: 0,
  status: 'needs-attention',
  analysis: '',
  sourceCount: 0,
};

const stellaCache = new Map<string, StellaRecommendationsCache>();
const processingCache = new Map<
  string,
  Promise<StellaRecommendationsData | null>
>();

const MAX_CACHE_SIZE = 50;
const CACHE_TTL = 30 * 60 * 1000;

const cleanupMemoryCache = () => {
  const now = Date.now();

  for (const [key, cached] of stellaCache.entries()) {
    if (now - cached.timestamp > CACHE_TTL) {
      stellaCache.delete(key);
    }
  }

  if (stellaCache.size > MAX_CACHE_SIZE) {
    const sortedEntries = Array.from(stellaCache.entries()).sort(
      ([, entryA], [, entryB]) => entryA.timestamp - entryB.timestamp
    );
    const toDelete = sortedEntries.slice(
      0,
      sortedEntries.length - MAX_CACHE_SIZE
    );
    toDelete.forEach(([key]) => stellaCache.delete(key));
  }
};

const checkProductContext = async (
  client: SupabaseClient<Database>,
  productId: string
): Promise<boolean> => {
  try {
    const [
      productResult,
      filesResult,
      projectsResult,
      feedbackResult,
      researchResult,
      toolLinksResult,
      experimentsResult,
    ] = await Promise.allSettled([
      client
        .from('products')
        .select('context_urls')
        .eq('id', productId)
        .maybeSingle(),
      client.from('product_files').select('id').eq('product_id', productId),
      client.from('projects').select('id').eq('product_id', productId),
      client.from('customer_feedback').select('id').eq('product_id', productId),
      client.from('market_research').select('id').eq('product_id', productId),
      client
        .from('product_tool_links')
        .select('id')
        .eq('product_id', productId),
      client
        .from('experiment_product_links')
        .select('id')
        .eq('product_id', productId),
    ]);

    const product =
      productResult.status === 'fulfilled' ? productResult.value.data : null;
    const files =
      filesResult.status === 'fulfilled' ? filesResult.value.data : [];
    const projects =
      projectsResult.status === 'fulfilled' ? projectsResult.value.data : [];
    const feedback =
      feedbackResult.status === 'fulfilled' ? feedbackResult.value.data : [];
    const research =
      researchResult.status === 'fulfilled' ? researchResult.value.data : [];
    const toolLinks =
      toolLinksResult.status === 'fulfilled' ? toolLinksResult.value.data : [];
    const experiments =
      experimentsResult.status === 'fulfilled'
        ? experimentsResult.value.data
        : [];

    return hasAnyContext({
      product,
      files,
      projects,
      feedback,
      marketResearch: research,
      linkedTools: toolLinks,
      experiments,
    });
  } catch (error) {
    console.error('Error checking product context:', error);
    return false;
  }
};

const EMPTY_STELLA_DATA: StellaRecommendationsData = {
  healthScore: DEFAULT_HEALTH_SCORE,
  marketOpportunities: [],
  customerNeeds: [],
  recommendedExperiments: [],
  strategicRisks: [],
  toolRecommendations: [],
  isLoading: false,
  error: null,
  lastUpdated: null,
};

export const useStellaRecommendations = (productId: string) => {
  const [data, setData] =
    useState<StellaRecommendationsData>(EMPTY_STELLA_DATA);

  const { toast } = useToast();
  const { getToken } = useAuth();
  const authedSupabase = useSupabaseClient();

  const fetchRecommendations = useCallback(
    async (
      forceRefresh: boolean = false
    ): Promise<StellaRecommendationsData | null> => {
      if (!forceRefresh && processingCache.has(productId)) {
        console.log(
          '⏭️ Request already processing, returning existing promise'
        );
        return processingCache.get(productId)!;
      }

      console.log('🚀 Starting new recommendation fetch');

      const processingPromise = (async () => {
        setData((prev: StellaRecommendationsData) => ({
          ...prev,
          isLoading: true,
          error: null,
        }));

        if (!forceRefresh) {
          const hasContext = await checkProductContext(
            authedSupabase,
            productId
          );
          if (!hasContext) {
            const emptyData: StellaRecommendationsData = {
              healthScore: DEFAULT_HEALTH_SCORE,
              marketOpportunities: [],
              customerNeeds: [],
              recommendedExperiments: [],
              strategicRisks: [],
              toolRecommendations: [],
              isLoading: false,
              error: null,
              lastUpdated: new Date().toISOString(),
            };
            setData(emptyData);
            return emptyData;
          }
        }

        if (!forceRefresh) {
          const cached = stellaCache.get(productId);
          if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
            setData({
              ...cached.data,
              healthScore: {
                ...DEFAULT_HEALTH_SCORE,
                ...cached.data.healthScore,
              },
              isLoading: false,
            });
            return cached.data;
          } else if (cached) {
            stellaCache.delete(productId);
          }
        }

        try {
          const { data: response, error } = await supabase.functions.invoke(
            'stella-api/analyze/product',
            {
              body: {
                productId,
                forceRefresh,
              },
              headers: {
                Authorization: `Bearer ${await getToken()}`,
              },
            }
          );

          if (error || !response) {
            const errorMessage =
              error instanceof Error
                ? error.message
                : String(error ?? 'Empty response from server');
            console.error('Stella recommendations failed:', errorMessage);

            const messages = getErrorMessages(
              error ?? new Error(errorMessage),
              {
                rateLimitContext: 'analyses',
                genericAction: 'analyze your product',
              }
            );

            setData((prev: StellaRecommendationsData) => ({
              ...prev,
              isLoading: false,
              error: errorMessage || 'Failed to fetch recommendations',
            }));

            toast({
              title: messages.title,
              description: messages.description,
              variant: 'destructive',
            });
            return null;
          }

          const newData: StellaRecommendationsData = {
            healthScore: {
              ...DEFAULT_HEALTH_SCORE,
              ...response.healthScore,
            },
            marketOpportunities: Array.isArray(response.marketOpportunities)
              ? response.marketOpportunities
              : [],
            customerNeeds: Array.isArray(response.customerNeeds)
              ? response.customerNeeds
              : [],
            recommendedExperiments: Array.isArray(
              response.recommendedExperiments
            )
              ? response.recommendedExperiments
              : [],
            strategicRisks: Array.isArray(response.strategicRisks)
              ? response.strategicRisks
              : [],
            toolRecommendations: Array.isArray(response.toolRecommendations)
              ? response.toolRecommendations
              : [],
            isLoading: false,
            error: null,
            lastUpdated: new Date().toISOString(),
          };

          setData(newData);

          stellaCache.set(productId, {
            data: newData,
            fingerprint: '',
            timestamp: Date.now(),
          });

          return newData;
        } catch (error) {
          console.error('Stella recommendations failed:', error);

          const messages = getErrorMessages(error, {
            rateLimitContext: 'analyses',
            genericAction: 'analyze your product',
          });

          setData((prev: StellaRecommendationsData) => ({
            ...prev,
            isLoading: false,
            error:
              error instanceof Error ? error.message : 'Unknown error occurred',
          }));

          toast({
            title: messages.title,
            description: messages.description,
            variant: 'destructive',
          });
          return null;
        }
      })();

      processingCache.set(productId, processingPromise);

      try {
        const result = await processingPromise;
        return result;
      } finally {
        processingCache.delete(productId);
      }
    },
    [productId, toast, getToken, authedSupabase]
  );

  useEffect(() => {
    if (!productId) {
      setData(EMPTY_STELLA_DATA);
      return;
    }

    const initializeData = async () => {
      cleanupMemoryCache();

      const memCached = stellaCache.get(productId);
      if (memCached && Date.now() - memCached.timestamp < CACHE_TTL) {
        setData(memCached.data);
        return;
      }

      if (processingCache.has(productId)) {
        const result = await processingCache.get(productId);
        if (result) {
          setData(result);
        }
        return;
      }
      console.log('🔄 No cache, setting loading state');
      setData({
        healthScore: DEFAULT_HEALTH_SCORE,
        marketOpportunities: [],
        customerNeeds: [],
        recommendedExperiments: [],
        strategicRisks: [],
        toolRecommendations: [],
        isLoading: true,
        error: null,
        lastUpdated: null,
      });

      if (memCached) {
        stellaCache.delete(productId);
      }

      const hasContext = await checkProductContext(authedSupabase, productId);
      if (hasContext) {
        fetchRecommendations(false);
      } else {
        setData((prev: StellaRecommendationsData) => ({
          ...prev,
          isLoading: false,
        }));
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, authedSupabase]);

  const refresh = useCallback(() => {
    cleanupMemoryCache();
    stellaCache.delete(productId);
    processingCache.delete(productId);
    return fetchRecommendations(true);
  }, [productId, fetchRecommendations]);

  return {
    data,
    fetchRecommendations,
    refresh,
  };
};
